import React, { useState } from 'react'
import { twMerge } from 'tailwind-merge'

import { useMeasuredTriggerWidth } from '../../useMeasuredTriggerWidth'
import { Box } from '../box'
import { Checkbox, CheckboxIndicator } from '../checkbox'
import { Icon, type IconSource } from '../icon'
import { Popover, PopoverBackdrop, PopoverContent } from '../popover'
import { Pressable } from '../pressable'
import { ChevronDownGlyph, GLYPH_COLORS, type GlyphTone } from '../primitive-glyphs/glyphs'
import { ScrollView } from '../scroll-view'
import { Text } from '../text'

export type MultiSelectOption = {
    value: string
    label: string
    /** Optional leading glyph, shown in the menu row and on a single-selection trigger. */
    icon?: IconSource
}

export type MultiSelectSize = 'sm' | 'md'

export type MultiSelectMenuHeight = 'default' | 'tall'

export type MultiSelectProps = {
    options: MultiSelectOption[]
    value: string[]
    onChange: (value: string[]) => void
    allLabel?: string
    summaryLabel?: string
    /**
     * Renders the trigger label from the current selection, overriding the
     * built-in all/count summary. Needed where "nothing selected" is meaningful
     * in its own right — a table filter shows "All" for an empty selection,
     * where the default would show `0`.
     */
    summary?: (selected: MultiSelectOption[]) => string
    renderOption?: (option: MultiSelectOption, isSelected: boolean) => React.ReactNode
    selectAllLabel?: string
    disabled?: boolean
    preventEmpty?: boolean
    size?: MultiSelectSize
    menuHeight?: MultiSelectMenuHeight
    /** Chrome ink for the chevron glyph. */
    glyphTone?: GlyphTone
    className?: string
    testID?: string
    nativeID?: string
}

const triggerSizeClasses: Record<MultiSelectSize, string> = {
    sm: 'h-7 gap-1 px-2',
    md: 'h-10 gap-2 px-3',
}
const menuHeightClasses: Record<MultiSelectMenuHeight, string> = {
    default: 'max-h-[240px]',
    tall: 'max-h-[480px]',
}
// Trigger and menu rows share one step: the Figma ListItem label is P2-Regular,
// which is what the `md` control renders. A flat `body` here would grow every
// `sm` menu past its own caption trigger.
const labelSize = { sm: 'caption', md: 'body' } as const
const chevronSize: Record<MultiSelectSize, number> = { sm: 16, md: 24 }

/**
 * Compact multi-select dropdown — the same neutral pill + anchored Popover as
 * {@link StatusDropdown}, but each menu row toggles membership (checkbox) and the
 * trigger shows an all/partial-count summary. Domain-agnostic: the caller maps
 * its enum to `{ value, label }` options.
 */
export const MultiSelect: React.FC<MultiSelectProps> = ({
    options,
    value,
    onChange,
    allLabel,
    summaryLabel,
    summary,
    renderOption,
    selectAllLabel,
    disabled = false,
    preventEmpty = false,
    size = 'sm',
    menuHeight = 'default',
    glyphTone = 'default',
    className,
    testID,
    nativeID,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    // A caller may map an empty selection back to a non-empty value ("no filter"
    // renders every row ticked). The menu then holds the cleared state itself
    // until an option is picked or it closes, so unticking "All" visibly clears.
    const [isCleared, setIsCleared] = useState(false)
    const { triggerWidth, measureTriggerRef } = useMeasuredTriggerWidth()
    const menuValue = isCleared ? [] : value
    const allSelected = options.length > 0 && value.length === options.length
    const menuAllSelected = options.length > 0 && menuValue.length === options.length
    const selectedOptions = options.filter(option => value.includes(option.value))
    const triggerLabel = summary
        ? summary(selectedOptions)
        : allSelected
          ? (allLabel ?? String(value.length))
          : summaryLabel
            ? `${summaryLabel}: ${value.length}`
            : String(value.length)
    // Only a lone selection gets its glyph on the trigger — with several selected
    // the label collapses to a count, which no single icon represents.
    const triggerIcon =
        !allSelected && selectedOptions.length === 1 ? selectedOptions[0].icon : undefined

    const commit = (next: string[]) => {
        if (preventEmpty && next.length === 0) {
            return
        }
        setIsCleared(next.length === 0)
        onChange(next)
    }

    const close = () => {
        setIsOpen(false)
        setIsCleared(false)
    }

    const toggle = (next: string) =>
        commit(menuValue.includes(next) ? menuValue.filter(v => v !== next) : [...menuValue, next])

    const toggleAll = () =>
        commit(
            menuAllSelected
                ? preventEmpty
                    ? options.slice(0, 1).map(o => o.value)
                    : []
                : options.map(o => o.value),
        )

    return (
        <Popover
            isOpen={isOpen && !disabled}
            onClose={close}
            trapFocus={false}
            focusScope={false}
            placement='bottom left'
            trigger={triggerProps => {
                const { ref: popoverRef, ...rest } = triggerProps as {
                    ref?: React.Ref<unknown>
                }
                return (
                    <Pressable
                        {...rest}
                        ref={measureTriggerRef(popoverRef)}
                        nativeID={nativeID}
                        testID={testID}
                        disabled={disabled}
                        onPress={() => (isOpen ? close() : setIsOpen(true))}
                        accessibilityRole='button'
                        accessibilityState={{ disabled, expanded: isOpen }}
                        className={twMerge(
                            'min-w-[116px] flex-row items-center justify-between rounded border border-solid border-border-subtle bg-aos-bg-input',
                            triggerSizeClasses[size],
                            disabled
                                ? 'pointer-events-none opacity-40'
                                : 'hover:border-border-strong',
                            className,
                        )}
                    >
                        {/* Clamped to one line — see the same note in StatusDropdown: with a
                        caller-constrained width a long summary otherwise wraps out of the
                        fixed-height pill. */}
                        <Box className='min-w-0 shrink flex-row items-center gap-2'>
                            {triggerIcon && (
                                <Icon
                                    as={triggerIcon}
                                    size={chevronSize[size]}
                                    className='text-aos-text-muted'
                                />
                            )}
                            <Text
                                size={labelSize[size]}
                                tone='default'
                                numberOfLines={1}
                                className='min-w-0 shrink'
                            >
                                {triggerLabel}
                            </Text>
                        </Box>
                        <ChevronDownGlyph
                            size={chevronSize[size]}
                            color={GLYPH_COLORS[glyphTone]}
                        />
                    </Pressable>
                )
            }}
        >
            <PopoverBackdrop />
            <PopoverContent className='w-auto max-w-none rounded border border-solid border-border-subtle bg-aos-bg-elevation py-1'>
                {/* ScrollView, not a Box with `overflow-scroll`: RN only honours
                    visible/hidden, so a plain View would cap the list and make the
                    overflow unreachable on native. */}
                {/* `minWidth`, not `width`: see the same note in StatusDropdown — the panel
                    tracks its widest row, down to the trigger's width and no further. */}
                <ScrollView
                    style={triggerWidth ? { minWidth: triggerWidth } : undefined}
                    className={twMerge('self-stretch', menuHeightClasses[menuHeight])}
                >
                    {selectAllLabel !== undefined && (
                        <Pressable
                            onPress={toggleAll}
                            accessibilityRole='menuitem'
                            accessibilityState={{ checked: menuAllSelected }}
                            aria-checked={menuAllSelected}
                            className='flex-row items-center gap-2 px-3 py-2 hover:bg-aos-bg-hover-layer'
                        >
                            <Checkbox
                                value='__all__'
                                isChecked={menuAllSelected}
                                size='sm'
                                className='pointer-events-none shrink-0'
                            >
                                <CheckboxIndicator />
                            </Checkbox>
                            <Text size={labelSize[size]} tone='default'>
                                {selectAllLabel}
                            </Text>
                        </Pressable>
                    )}
                    {options.map(option => {
                        const isSelected = menuValue.includes(option.value)
                        return (
                            <Pressable
                                key={option.value}
                                onPress={() => toggle(option.value)}
                                accessibilityRole='menuitem'
                                accessibilityState={{ checked: isSelected }}
                                aria-checked={isSelected}
                                className='flex-row items-center gap-2 px-3 py-2 hover:bg-aos-bg-hover-layer'
                            >
                                <Checkbox
                                    value={option.value}
                                    isChecked={isSelected}
                                    size='sm'
                                    className='pointer-events-none shrink-0'
                                >
                                    <CheckboxIndicator />
                                </Checkbox>
                                {renderOption ? (
                                    renderOption(option, isSelected)
                                ) : (
                                    <>
                                        {option.icon && (
                                            <Icon
                                                as={option.icon}
                                                size={16}
                                                className='text-aos-text-default'
                                            />
                                        )}
                                        {/* The panel grows to its longest label, but a placement flip
                                    can still cap it. Ellipsise rather than hard-clipping
                                    mid-word: `numberOfLines` is what native reads, `truncate`
                                    supplies text-overflow on web. */}
                                        <Text
                                            size={labelSize[size]}
                                            tone='default'
                                            numberOfLines={1}
                                            className='shrink truncate'
                                        >
                                            {option.label}
                                        </Text>
                                    </>
                                )}
                            </Pressable>
                        )
                    })}
                </ScrollView>
            </PopoverContent>
        </Popover>
    )
}

MultiSelect.displayName = 'MultiSelect'
